import { User } from '@property.com.au/argonaut';
import { DataGetter, trackView } from '@rea-argonaut/analytics';
import { getCampaignData, getPageData } from '@realestate.com.au/iglu-data';
import { useEffect } from 'react';
import { deepOmitTypeName } from '../helpers/deepOmitTypeName';
import { getPcaUserData } from '../helpers/getPcaUserData';
import { objectRemoveEmptyDeep } from '../helpers/objectRemoveEmptyDeep';
import { EcoFriendlyFeature, IndoorFeature, PropertyStatus, PropertyType, SortBy } from '../types';

export type PageData = {
  pageType: string;
  site: string;
  siteSection: string;
};

type PCASearchResultParameters = {
  schema: string;
  data: {
    locations_requested: string[] | null;
    include_surrounding_suburbs: boolean | null;
    property_status: PropertyStatus | null;
    property_types: PropertyType[] | null;
    bedrooms_min: string | null;
    bathrooms_min: number | null;
    land_min: number | null;
    indoor_features: IndoorFeature[] | null;
    outdoor_features: string[] | null;
    eco_friendly_features: EcoFriendlyFeature[] | null;
    car_spaces_min: number | null;
    keywords: (string | null)[] | null;
    exclude_deposit_taken: boolean | null;
    exclude_properties_without_image?: boolean | null;
    exclude_under_contract: boolean | null;
    sort_by: SortBy | null;
    price_min: number | null;
    price_max: number | null;
    search_model_variant?: string | null;
    commutes?:
      | {
          point_of_interest: string;
          transportation_method: 'public_transport' | 'driving' | 'walking' | 'cycling';
          commute_time_in_minutes: number;
        }[]
      | null;
  };
};

type PCASearchResults = {
  schema: string;
  data: {
    results_total: number;
    page_number: number;
    results_count: number;
    results?:
      | {
          id: string;
          type: string;
          index: number;
        }[]
      | null;
  };
};

interface UseTrackViewProps {
  user: User;
  pageData: PageData;
  useUserContext?: boolean;
  pcaSearchResultParameters: PCASearchResultParameters;
  pcaSearchResults: PCASearchResults | null;
}

export const useTrackView = ({
  user,
  pageData,
  useUserContext,
  pcaSearchResultParameters,
  pcaSearchResults,
}: UseTrackViewProps) => {
  useEffect(() => {
    const contexts: DataGetter[] = [
      getPageData(pageData),
      getCampaignData(),
      () => ({
        pca_search_results_parameters: objectRemoveEmptyDeep(
          deepOmitTypeName(pcaSearchResultParameters),
        ),
      }),
      () => ({
        ...(pcaSearchResults && {
          pca_search_results: objectRemoveEmptyDeep(deepOmitTypeName(pcaSearchResults)),
        }),
      }),
    ];

    if (useUserContext && user) {
      contexts.push(getPcaUserData(user));
    }
    trackView(contexts);
  }, [pageData, pcaSearchResultParameters, pcaSearchResults, useUserContext, user]);
};
